import { useEffect, useState } from "react";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StockService } from "../../services";
import { dateOnly, dateTime } from "../../utils/format";

const eventLabels = {
  STOCK_ADDED: "Stock Added",
  CUTOVER_OPENING: "Tracking Opening Balance",
  SALE: "Sold",
  SALE_RESTORED: "Sale Restored",
  ADJUSTMENT_INCREASE: "Adjustment Increase",
  ADJUSTMENT_DECREASE: "Adjustment Decrease",
  ADJUSTMENT_REVERSAL: "Adjustment Reversal",
  CORRECTION_RESTORED: "Correction Restored",
  CORRECTION_REDUCTION: "Correction Deduction",
  OPENED_FOR_MEASURE: "Opened for Measure",
  BATCH_QUANTITY_INCREASE: "Batch Quantity Increased",
  BATCH_QUANTITY_DECREASE: "Batch Quantity Decreased",
};

const quantityLabel = (quantity) => `${quantity > 0 ? "+" : ""}${quantity}`;

export const BatchJourneyDialog = ({ open, onOpenChange, source, batchId }) => {
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !source || !batchId) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    setJourney(null);
    StockService.journey(source, batchId)
      .then((result) => { if (active) setJourney(result); })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || "Could not load this batch journey.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, source, batchId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-hidden p-5 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-left font-display text-navy-900">Stock Journey</DialogTitle>
          <DialogDescription className="text-left">Batch stock movements recorded in Sri Lanka business time.</DialogDescription>
        </DialogHeader>

        <div className="min-h-0">
          {loading && <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading journey…</div>}
          {!loading && error && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>}
          {!loading && journey && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                {[
                  ["Product", journey.productName],
                  ["Batch / Invoice", journey.batchReference || `#${journey.batchId}`],
                  [journey.historyCompleteness === "PARTIAL" ? "Cutover Stock" : "Initial Stock", journey.openingQuantity],
                  ["Sold / Used", journey.totalSold],
                  ["Adjusted", journey.totalAdjusted],
                  ["Restored", journey.totalRestored],
                  ["Current Stock", journey.currentQuantity],
                ].map(([label, value]) => (
                  <div key={label} className="min-w-0 rounded-md border border-slate-200 bg-slate-50/70 px-2.5 py-2">
                    <div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
                    <div className="mt-0.5 break-words text-sm font-semibold text-slate-800">{value ?? "—"}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                <span>{journey.stockSource} batch · Purchased {dateOnly(journey.purchaseDate)}</span>
                <span className={`font-medium ${journey.reconciled ? "text-emerald-700" : "text-rose-700"}`}>
                  {journey.reconciled ? "Ledger reconciles with current stock" : "Ledger does not reconcile with current stock"}
                </span>
              </div>

              {journey.historyCompleteness === "PARTIAL" && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>Partial history before tracking began. The opening balance below is the stock available at cutover; earlier movements were not reconstructed.</span>
                </div>
              )}

              <div className="max-h-[48vh] overflow-y-auto pr-2">
                {journey.events?.length ? (
                  <ol className="space-y-0">
                    {journey.events.map((event, index) => {
                      const incoming = event.quantityChange > 0;
                      const Icon = incoming ? ArrowUpRight : ArrowDownRight;
                      return (
                        <li key={event.movementId} className="relative grid grid-cols-[30px_minmax(0,1fr)] gap-3 pb-4 last:pb-1">
                          {index < journey.events.length - 1 && <span className="absolute bottom-0 left-[14px] top-7 w-px bg-slate-200" aria-hidden="true" />}
                          <span className={`relative z-10 grid h-7 w-7 place-items-center rounded-full border ${incoming ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600"}`}>
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <div className="min-w-0 rounded-md border border-slate-200 bg-white px-3 py-2">
                            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                              <div className="font-semibold text-slate-800">{eventLabels[event.eventType] || event.eventType} <span className={incoming ? "text-emerald-700" : "text-slate-700"}>{quantityLabel(event.quantityChange)}</span></div>
                              <time className="text-xs text-slate-500">{dateTime(event.movementDateTime)}</time>
                            </div>
                            {(event.billNumber || event.reason || event.reference || event.actor || event.outletCode) && (
                              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-600">
                                {event.billNumber && <span>{event.billNumber}{event.outletCode ? ` · ${event.outletCode}` : ""}</span>}
                                {event.reason && <span>{event.reason}</span>}
                                {event.reference && <span>{event.reference}</span>}
                                {event.actor && <span>By {event.actor}</span>}
                                {event.correctionRequestId && <span>Correction #{event.correctionRequestId}</span>}
                                {event.adjustmentId && <span>Adjustment #{event.adjustmentId}</span>}
                              </div>
                            )}
                            {event.saleDateTime && event.saleDateTime !== event.movementDateTime && (
                              <div className="mt-1 text-xs text-slate-500">Sale time: {dateTime(event.saleDateTime)}</div>
                            )}
                            <div className="mt-1 text-xs font-medium text-slate-700">Balance: {event.quantityAfter}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : <p className="rounded-md border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">No stock movements are available for this batch.</p>}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
